import { type FC, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { listScheduledPosts } from '../../api/client.js';
import {
  Search,
  Moon,
  Sun,
  Smartphone,
  Users,
  CalendarClock,
  LogOut,
  Settings,
  User,
  Command,
  Bot,
  Activity,
} from 'lucide-react';
import { cn } from '../../lib/utils.js';
import { useUIStore } from '../../store/ui-store.js';
import { useAuthStore } from '../../store/auth-store.js';
import { useDeviceStore } from '../../store/device-store.js';
import { useWebSocket } from '../../hooks/useWebSocket.js';
import { useAiStatus } from '../../hooks/use-ai-status.js';
import { Avatar, AvatarFallback } from '../ui/avatar.js';
import { StatusDot } from '../ui/status-dot.js';
import { Badge } from '../ui/badge.js';
import { AlertBell } from './alert-bell.js';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../ui/dropdown-menu.js';

const navLabels: Record<string, string> = {
  '/': 'Dashboard',
  '/hub/content': 'Media Library',
  '/hub/upload': 'Upload',
  '/hub/collections': 'Collections',
  '/content/approvals': 'Content Approvals',
  '/creator/content-approvals': 'Approve My Content',
  '/creators': 'Creators',
  '/posts': 'Scheduled Posts',
  '/devices': 'Devices',
  '/devices/live-wall': 'Live Wall',
  '/agents': 'Connectors',
  '/activity': 'Activity',
  '/audit': 'Audit Log',
  '/health': 'System Health',
  '/settings': 'Settings',
};

interface GlobalHeaderProps {
  currentPath: string;
}

export const GlobalHeader: FC<GlobalHeaderProps> = ({ currentPath }) => {
  const navigate = useNavigate();
  const { data: aiStatus } = useAiStatus();
  const { theme, toggleTheme, setCommandPaletteOpen } = useUIStore();
  const { user, logout } = useAuthStore();
  const { connected } = useWebSocket();
  const devices = useDeviceStore((s) => s.devices);

  const { data: queuedPosts } = useQuery({
    queryKey: ['header-queued-posts'],
    queryFn: () => listScheduledPosts({ status: 'QUEUED' }),
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: false,
  });
  const deviceCount = devices?.devices.length ?? 0;
  const onlineDevices = devices?.devices.filter((d) => d.connectionStatus === 'CONNECTED').length ?? 0;

  const pageTitle = navLabels[currentPath] ?? 'LaxmiAI';

  // Command palette keyboard shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [setCommandPaletteOpen]);

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <header className="flex h-14 items-center justify-between border-b border-slate-800 bg-slate-900/80 px-4 backdrop-blur-sm">
      {/* Left: Page title */}
      <div className="flex items-center gap-3">
        <h1 className="text-base font-semibold text-slate-100">{pageTitle}</h1>
      </div>

      {/* Center: quick navigation (the old free-text search led to a page that does not exist) */}
      <button
        type="button"
        onClick={() => setCommandPaletteOpen(true)}
        className="hidden h-9 w-72 items-center gap-2 rounded-lg border border-slate-800 bg-slate-800/50 px-3 text-sm text-slate-500 hover:border-slate-700 md:flex"
      >
        <Search className="h-4 w-4" />
        <span className="flex-1 text-left">Go to…</span>
        <kbd className="flex items-center gap-0.5 rounded border border-slate-700 bg-slate-800 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">
          <Command className="h-2.5 w-2.5" />K
        </kbd>
      </button>

      {/* Right: Status + Actions */}
      <div className="flex items-center gap-2">
        {/* AI status: what GET /api/v1/ai/status says, never a static label */}
        <button
          type="button"
          onClick={() => navigate('/settings')}
          title={aiStatus === undefined ? 'AI status' : aiStatus.enabled ? `AI on: ${aiStatus.model ?? aiStatus.provider ?? ''}` : 'AI is off on this server'}
          className="hidden items-center gap-1.5 rounded-lg bg-slate-800/50 px-2.5 py-1.5 hover:bg-slate-800 md:flex"
        >
          <Bot className={cn('h-3.5 w-3.5', aiStatus?.enabled === true ? 'text-blue-400' : 'text-slate-600')} />
          <span className="text-[11px] font-medium text-slate-400">
            {aiStatus === undefined ? 'AI' : aiStatus.enabled ? 'AI ready' : 'AI off'}
          </span>
        </button>

        {/* Connected devices */}
        <div className="hidden items-center gap-1.5 rounded-lg bg-slate-800/50 px-2.5 py-1.5 md:flex">
          <Smartphone className="h-3.5 w-3.5 text-emerald-400" />
          <span className="text-[11px] font-medium text-slate-400">
            {onlineDevices}/{deviceCount}
          </span>
        </div>

        {/* Scheduled posts: a real count of posts waiting to publish (it was a
            fixed "12"). Hidden when the count is unavailable, e.g. for a role
            that cannot see scheduled posts. */}
        {queuedPosts !== undefined && (
          <button
            type="button"
            onClick={() => navigate('/posts')}
            title="Posts waiting to be published"
            className="hidden items-center gap-1.5 rounded-lg bg-slate-800/50 px-2.5 py-1.5 hover:bg-slate-800 lg:flex"
          >
            <CalendarClock className="h-3.5 w-3.5 text-amber-400" />
            <span className="text-[11px] font-medium text-slate-400">{queuedPosts.total}</span>
          </button>
        )}

        {/* WebSocket status */}
        <StatusDot status={connected ? 'online' : 'offline'} pulse={connected} className="mx-1" />

        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-800 hover:text-slate-300"
        >
          {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>

        {/* Alerts */}
        <AlertBell />

        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 rounded-lg p-1 hover:bg-slate-800">
              <Avatar className="h-7 w-7">
                <AvatarFallback>{(user?.name ?? 'U').charAt(0).toUpperCase()}</AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel>{user?.name ?? 'User'}</DropdownMenuLabel>
            <DropdownMenuLabel className="text-xs font-normal text-slate-500 -mt-1">{user?.email}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/settings')}>
              <Settings className="mr-2 h-4 w-4" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
